
import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BarChartIcon from "@mui/icons-material/BarChart";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import ProductManagementDetail from "./ProductManagementDetail";
import { commonRunQuery } from "../../util/API/loans";
import { PortfolioGetDelegateOverview } from "../../util/API/portfolio";
// ── Reusable empty-state row ──
const EmptyStateRow = ({ colSpan, message }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 5 }}>
      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
        <Typography variant="body2" color="textSecondary">
          {message || "No results found."}
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
);

const InvestmentsTable = ({
  onRowClick,
  overrideRole,
  selectedInvestorDetails,
  onBack,
  onViewInvestmentDetails,
  onViewDocs,
}) => {
  const userId = JSON.parse(sessionStorage.getItem("user"))?.userid;
  const [userRole, setUserRole] = useState("investor");
  const configVariables = useSelector((state) => state.config.baseConfig);
  const userSecurityRoles = JSON.parse(sessionStorage.getItem("user"))?.payload
    ?.body?.Securityuserrole;

  const [managingProduct, setManagingProduct] = useState(null);
  const [investmentRows, setInvestmentRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [investmentsPlans, setInvestmentsPlans] = useState([]);
  const [investorRows, setInvestorRows] = useState([]);
  const [investorDelegates, setInvestorDelegates] = useState([]);

  // ── Investor fund drill-down (admin) ──
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [investorFunds, setInvestorFunds] = useState([]);
  const [investorFundsLoading, setInvestorFundsLoading] = useState(false);

  const activeRole = overrideRole || userRole;
  const investorFundsRef = useRef(null);

  // ── Pagination: All Investment Funds ──
  const [fundsPage, setFundsPage] = useState(0);
  const [fundsRowsPerPage, setFundsRowsPerPage] = useState(5);
  const visibleFundsRows = investmentsPlans.slice(
    fundsPage * fundsRowsPerPage,
    fundsPage * fundsRowsPerPage + fundsRowsPerPage,
  );

  // ── Pagination: Primary table (investors / portfolio) ──
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const primaryData = activeRole === "admin" ? investorRows : investmentRows;
  const visiblePrimaryRows = primaryData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // ── Pagination: Investor fund drill-down ──
  const [iPage, setIPage] = useState(0);
  const [iRowsPerPage, setIRowsPerPage] = useState(5);
  const visibleInvestorFunds = investorFunds.slice(
    iPage * iRowsPerPage,
    iPage * iRowsPerPage + iRowsPerPage,
  );

  // ── Pagination: Delegates ──
  const [delegatesPage, setDelegatesPage] = useState(0);
  const [delegatesRowsPerPage, setDelegatesRowsPerPage] = useState(5);
  const visibleDelegates = investorDelegates.slice(
    delegatesPage * delegatesRowsPerPage,
    delegatesPage * delegatesRowsPerPage + delegatesRowsPerPage,
  );

  // ── Effects ──
  useEffect(() => {
    getInvestmentPortfolioOfUser();
  }, [userId]);
  useEffect(() => {
    getInvestments();
  }, []);
  useEffect(() => {
    getRegisteredInvestors();
  }, []);
  useEffect(() => {
    getDelegates();
  }, []);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleFundsChangePage = (_, newPage) => setFundsPage(newPage);
  const handleFundsChangeRowsPerPage = (e) => {
    setFundsRowsPerPage(parseInt(e.target.value, 10));
    setFundsPage(0);
  };

  if (managingProduct) {
    return (
      <ProductManagementDetail
        product={managingProduct}
        onBack={() => setManagingProduct(null)}
      />
    );
  }

  // ── Query 363: investor's own portfolio ──
 const getInvestmentPortfolioOfUser = async () => {
  console.log("Enter the function to get investments portfolio");
  try {
    const [response, delegateResponse] = await Promise.all([
      commonRunQuery(363, userId),
      PortfolioGetDelegateOverview("", userId),
    ]);

    const allRows = [];

    // --- Original portfolio records ---
    const record = response?.data?.body?.record;
    if (record) {
      const recordsArray = Array.isArray(record) ? record : [record];
      const mapped = recordsArray.map((item) => {
        const fund = item.f;
        const stats = item.record;
        return {
          name: fund?.fund_name || "—",
          committed: stats?.total_committed != null
            ? `$${Number(stats.total_committed).toLocaleString()}`
            : "—",
          called: stats?.total_called != null
            ? `$${Number(stats.total_called).toLocaleString()}`
            : "—",
          uncalled: stats?.total_uncalled != null
            ? `$${Number(stats.total_uncalled).toLocaleString()}`
            : "—",
          distributed: "—",
          tvpi: `${Number(stats?.avg_tvpi ?? 0).toFixed(2)}x`,
          dpi: `${Number(stats?.avg_dpi ?? 0).toFixed(2)}x`,
          rvpi: `${Number(stats?.avg_rvpi ?? 0).toFixed(2)}x`,
          units: "—",
          lastInvestmentDate: stats?.last_investment_date || "—",
        };
      });
      allRows.push(...mapped);
    }

    // --- ✅ Delegate overview records ---
    // Handle both single object and array (backend returns object for 1, array for many)
    const delegateItem = delegateResponse?.data?.fundOverview?.item;
    if (delegateItem) {
      const delegateArray = Array.isArray(delegateItem) ? delegateItem : [delegateItem];
      const mappedDelegate = delegateArray.map((item) => ({
        name: item?.fund_name || "—",
        committed: item?.total_committed != null
          ? `$${Number(item.total_committed).toLocaleString()}`
          : "—",
        called: item?.total_called != null
          ? `$${Number(item.total_called).toLocaleString()}`
          : "—",
        uncalled: item?.total_uncalled != null
          ? `$${Number(item.total_uncalled).toLocaleString()}`
          : "—",
        distributed: "—",
        tvpi: `${Number(item?.avg_tvpi ?? 0).toFixed(2)}x`,
        dpi: `${Number(item?.avg_dpi ?? 0).toFixed(2)}x`,
        rvpi: `${Number(item?.avg_rvpi ?? 0).toFixed(2)}x`,
        units: "—",
        lastInvestmentDate: item?.last_investment_date || "—",
      }));
      allRows.push(...mappedDelegate);
    }

    setInvestmentRows(allRows); //  merged both sources

  } catch (error) {
    console.error(error);
  }
};

  // ── Query 369: delegates assigned to current investor ──
  const getDelegates = async () => {
    try {
      const response = await commonRunQuery(369, userId);
      console.log("Delegates Response ????????????", JSON.stringify(response));

      const record = response?.data?.body?.record;
      if (record) {
        const recordsArray = Array.isArray(record) ? record : [record];
        const mapped = recordsArray.map((item) => {
          const u = item.u;
          const uea = item.uea;
          const idg = item.idg;
          return {
            id: u.delegate_id,
            // name: `${u.firstName} ${u.lastName}`,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.otherNames,
            email: uea.email,
            assignedDate: idg.assigned_date?.split(" ")[0] || "—",
            status: "Active",
          };
        });
        setInvestorDelegates(mapped);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ── Query 364: all registered investors (admin) ──
  const getRegisteredInvestors = async () => {
    try {
      const response = await commonRunQuery(364, "");
      console.log("Investors Response ============", response);

      const records = response?.data?.body?.record;
      if (records) {
        const recordsArray = Array.isArray(records) ? records : [records];
        const mapped = recordsArray.map((item) => {
          const u = item.u;
          const ue = item.ue;
          const record = item.record;
          const first = u.firstName?.trim();
          const last = u.lastName?.trim();

          return {
            id: u.investor_id,
            // name: `${u.firstName} ${u.lastName}`,
            name: `${first || ''} ${last || ''}`.trim() || u.otherNames,
            email: ue.email,
            totalInvested: `$${Number(record.total_invested_amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
            activeFunds: record.total_unique_funds,
            status: "Active",
          };
        });
        setInvestorRows(mapped);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ── Query 359: all funds (admin) ──
  const getInvestments = async () => {
    console.log("Enter the function to get investments");
    try {
      const response = await commonRunQuery(374, "");
      console.log("First Response ????????????", response);

      const records = response?.data?.body?.record;
      if (records) {
        const recordsArray = Array.isArray(records) ? records : [records];
        const mapped = recordsArray.map((item) => {
          // console.log("item===========",item);
          const fund = item.funds;
          const total_called = item.record.called_total;
          const total_uncalled = item.record.uncalled_total;
          return {
            id: fund.id,
            title: fund.name,
            minEntry: `$${Number(fund.minimum_ticket_size).toLocaleString()}`,
            totalCommitted: `$${Number(fund.total_fund_size).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
            called: `$${Number(total_called).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
            uncalled: `$${Number(total_uncalled).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
            status: "Active",
            creator: fund.created_by_role,
            description: fund.description,
            totalFundSize: `$${Number(fund.total_fund_size).toLocaleString()}`,
            targetMultiple: fund.target_multiple,
            targetYearlyReturn: `${fund.target_yearly_return}%`,
            tvpi: fund.tvpi,
            dpi: fund.dpi,
            rvpi: fund.rvpi,
            videoUrl: fund.associated_video_url,
            startDate: fund.start_date,
            endDate: fund.end_date,
          };
        });
        setInvestmentsPlans(mapped);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ── Query 363: specific investor's funds (admin drill-down) ──
  const getInvestorFunds = async (investor) => {
    setSelectedInvestor(investor);
    setInvestorFunds([]);
    setInvestorFundsLoading(true);
    setIPage(0);
    setTimeout(() => {
      investorFundsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
    try {
      const response = await commonRunQuery(363, investor.id);
      console.log("Q363 Investor Funds Response >>>", JSON.stringify(response));

      const records = response?.data?.body?.record;
      if (records) {
        const recordsArray = Array.isArray(records) ? records : [records];
        const mapped = recordsArray.map((item) => {
          const fund = item.f || item.fund || item.funds || {};
          const stats = item.record || item.stats || {};
          return {
            id: fund.id || fund.fund_id || "—",
            name: fund.fund_name || fund.name || "—",
            committed: stats.total_committed
              ? `$${Number(stats.total_committed).toLocaleString()}`
              : "—",
            called: stats.total_called
              ? `$${Number(stats.total_called).toLocaleString()}`
              : "—",
            uncalled: stats.total_uncalled
              ? `$${Number(stats.total_uncalled).toLocaleString()}`
              : "—",
            tvpi: stats.avg_tvpi
              ? `${Number(stats.avg_tvpi).toFixed(2)}x`
              : "—",
            dpi: stats.avg_dpi ? `${Number(stats.avg_dpi).toFixed(2)}x` : "—",
            rvpi: stats.avg_rvpi
              ? `${Number(stats.avg_rvpi).toFixed(2)}x`
              : "—",
            lastInvestmentDate: stats.last_investment_date || "—",
            status: "Active",
          };
        });
        setInvestorFunds(mapped);
      }
    } catch (error) {
      console.error("Error fetching investor funds:", error);
    } finally {
      setInvestorFundsLoading(false);
    }
  };

  return (
    <Box>
      {/* ── SECTION 1: ALL INVESTMENT FUNDS (Admin only) ── */}
      {activeRole === "admin" && !selectedInvestorDetails && (
        <Box className="dashboard-card" sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <BusinessCenterIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              All Investment Funds
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 2, display: "block" }}
          >
            Consolidated list of global funds and investor-submitted offerings.
          </Typography>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: "1px solid #eee" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Fund Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Total Committed
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Called</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Uncalled</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>TVPI</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>DPI</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>RVPI</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleFundsRows.length === 0 ? (
                  <EmptyStateRow
                    colSpan={9}
                    message="No investment funds found."
                  />
                ) : (
                  visibleFundsRows.map((product) => (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {product.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {product.type}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {product.totalCommitted}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {product.called}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {product.uncalled}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          color: "#1a73e8",
                        }}
                      >
                        {product.tvpi}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {product.dpi}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {product.rvpi}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.status}
                          size="small"
                          variant="outlined"
                          color={
                            product.status === "Active" ? "success" : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{
                            textTransform: "capitalize",
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => setManagingProduct(product)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={investmentsPlans.length}
            rowsPerPage={fundsRowsPerPage}
            page={fundsPage}
            onPageChange={handleFundsChangePage}
            onRowsPerPageChange={handleFundsChangeRowsPerPage}
          />
        </Box>
      )}

      {/* ── SECTION 2: REGISTERED INVESTORS / PORTFOLIO ── */}
      <Box className="dashboard-card" sx={{ mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Box display="flex" alignItems="center">
            {activeRole === "investor" && onBack && (
              <IconButton
                onClick={onBack}
                size="small"
                sx={{ mr: 1, bgcolor: "#f5f5f5" }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {activeRole === "admin"
                ? "Registered Investors"
                : "Investment Portfolio"}
              {activeRole === "investor" &&
                selectedInvestorDetails &&
                ` - ${selectedInvestorDetails.email}`}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ mb: 2, display: "block" }}
        >
          {activeRole === "admin"
            ? "Click on an investor to view their subscribed funds."
            : "Overview of current holdings and performance metrics."}
        </Typography>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid #eee" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                {activeRole === "admin" ? (
                  <>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Investor Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Total Invested
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Funds</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Investment
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Committed</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Called</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Uncalled</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>TVPI</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visiblePrimaryRows.length === 0 ? (
                <EmptyStateRow
                  colSpan={activeRole === "admin" ? 5 : 6}
                  message={
                    activeRole === "admin"
                      ? "No investors registered yet."
                      : "No investments in your portfolio yet."
                  }
                />
              ) : (
                visiblePrimaryRows.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={() => {
                      if (activeRole === "admin") getInvestorFunds(row);
                      else onRowClick && onRowClick(row);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    {activeRole === "admin" ? (
                      <>
                        <TableCell sx={{ fontWeight: 500, color: "#1976d2" }}>
                          {row.name}
                        </TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.totalInvested}</TableCell>
                        <TableCell>{row.activeFunds}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ fontWeight: 500, color: "#004a77" }}>
                          {row.name}
                        </TableCell>
                        <TableCell>{row.committed}</TableCell>
                        <TableCell>{row.called}</TableCell>
                        <TableCell>{row.uncalled}</TableCell>
                        <TableCell>{row.tvpi}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<BarChartIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewInvestmentDetails(row);
                              }}
                            >
                              Analytics
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<FolderOpenIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewDocs(row);
                              }}
                              sx={{
                                textTransform: "none",
                                borderColor: "#002e4c",
                                color: "#002e4c",
                                "&:hover": {
                                  borderColor: "#004a7a",
                                  backgroundColor: "rgba(0, 46, 76, 0.04)",
                                },
                              }}
                            >
                              View Docs
                            </Button>
                          </Stack>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={primaryData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      {/* ── SECTION 3: DELEGATES TABLE (investor only) ── */}
      {activeRole === "investor" && investorDelegates.length > 0 && (
        <Box className="dashboard-card" sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <GroupIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              My Delegates
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 2, display: "block" }}
          >
            Delegates assigned to manage your account.
          </Typography>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: "1px solid #eee" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Assigned Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleDelegates.length === 0 ? (
                  <EmptyStateRow colSpan={4} message="No delegates assigned." />
                ) : (
                  visibleDelegates.map((delegate, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontWeight: 500, color: "#004a77" }}>
                        {delegate.name}
                      </TableCell>
                      <TableCell>{delegate.email}</TableCell>
                      <TableCell>{delegate.assignedDate}</TableCell>
                      <TableCell>
                        <Chip
                          label={delegate.status}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={investorDelegates.length}
            rowsPerPage={delegatesRowsPerPage}
            page={delegatesPage}
            onPageChange={(_, newPage) => setDelegatesPage(newPage)}
            onRowsPerPageChange={(e) => {
              setDelegatesRowsPerPage(parseInt(e.target.value, 10));
              setDelegatesPage(0);
            }}
          />
        </Box>
      )}

      {/* ── SECTION 4: INVESTOR FUND DRILL-DOWN (admin only) ── */}
      {activeRole === "admin" && selectedInvestor && (
        <Box ref={investorFundsRef} className="dashboard-card" sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <IconButton
              size="small"
              sx={{ bgcolor: "#f5f5f5" }}
              onClick={() => {
                setSelectedInvestor(null);
                setInvestorFunds([]);
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <PersonIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {selectedInvestor.name}'s Subscribed Funds
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 2, display: "block" }}
          >
            Funds this investor is currently subscribed to.
          </Typography>

          {investorFundsLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <CircularProgress size={32} />
              <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                Loading funds…
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: "1px solid #eee" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Fund Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Committed
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Called</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Uncalled
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>TVPI</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>DPI</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>RVPI</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Last Investment
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleInvestorFunds.length === 0 ? (
                      <EmptyStateRow
                        colSpan={9}
                        message="No funds found for this investor."
                      />
                    ) : (
                      visibleInvestorFunds.map((fund, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ fontWeight: 500, color: "#004a77" }}>
                            {fund.name}
                          </TableCell>
                          <TableCell>{fund.committed}</TableCell>
                          <TableCell>{fund.called}</TableCell>
                          <TableCell>{fund.uncalled}</TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", color: "#1a73e8" }}
                          >
                            {fund.tvpi}
                          </TableCell>
                          <TableCell>{fund.dpi}</TableCell>
                          <TableCell>{fund.rvpi}</TableCell>
                          <TableCell>{fund.lastInvestmentDate}</TableCell>
                          <TableCell>
                            <Chip
                              label={fund.status}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={investorFunds.length}
                rowsPerPage={iRowsPerPage}
                page={iPage}
                onPageChange={(_, p) => setIPage(p)}
                onRowsPerPageChange={(e) => {
                  setIRowsPerPage(parseInt(e.target.value, 10));
                  setIPage(0);
                }}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default InvestmentsTable;


